import { createPortal } from 'react-dom'
import { Ban, ChevronDown } from 'lucide-react'
import { CompanyFilterPanel, DEFAULT_COMPANY_FILTER } from 'components/shared/CompanyFilterPanel'
import type { CompanyFilterOptions } from 'components/shared/LoadsTable/CompanyFilterButton'
import { useAnchoredPopover } from '../hooks/useAnchoredPopover'
import { isBoxTruckCompanyFilterActive } from '../utils/boxTruckFilterSummary'

/** Load-hub's CompanyFilterOptions, verbatim — the box-truck board only differs in HOW the blocked list
 *  is matched (beyond MC, since Sylectus loads carry none), which lives in utils/brokerListMatcher. */
export type BoxTruckCompanyFilterOptions = CompanyFilterOptions

export const EMPTY_COMPANY_FILTER: BoxTruckCompanyFilterOptions = DEFAULT_COMPANY_FILTER

/**
 * The box-truck "Broker Blacklist" filter: the shared CompanyFilterPanel behind a compact refinement-row
 * chip. Unlike load-hub's wrapper this one is fully controlled — the page owns the user/company-scoped,
 * persisted value and applies it live, so the panel needs no Apply step beyond Done closing it. The
 * panel is portaled (anchored popover) because the chip lives inside the board's overflow container.
 */
export const BoxTruckCompanyFilter = ({
    value,
    onChange,
    companies,
}: {
    value: BoxTruckCompanyFilterOptions
    onChange: (next: BoxTruckCompanyFilterOptions) => void
    /** Unique company names present on the loaded board — the search suggestions. */
    companies: string[]
}) => {
    const { open, anchorRect, containerRef, triggerRef, panelRef, close, togglePanel, panelPositionStyle } = useAnchoredPopover({ panelWidth: 340 })
    const active = isBoxTruckCompanyFilterActive(value)

    return (
        <div ref={containerRef} className="inline-flex">
            <button
                ref={triggerRef}
                type="button"
                onClick={togglePanel}
                title="Filter by company / blocked list"
                className={`flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium shadow-field transition-colors ${
                    active ? 'bg-accent-soft text-accent' : 'bg-surface text-foreground-muted hover:text-foreground'
                }`}
            >
                <Ban size={12} /> Broker Blacklist
                {value.companyNames.length > 0 && ` (${value.companyNames.length})`}
                {value.listType === 'blocked' && ' · Blocked'}
                {value.listType === 'preferred' && ' · Preferred'}
                <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open &&
                anchorRect &&
                createPortal(
                    <div ref={panelRef} style={panelPositionStyle} className="rounded-2xl bg-surface shadow-overlay" onClick={(e) => e.stopPropagation()}>
                        <CompanyFilterPanel value={value} onChange={onChange} companies={companies} title="Broker Blacklist" onDone={close} />
                    </div>,
                    document.body
                )}
        </div>
    )
}
